import type { ForeignExchangeRate, Holding } from '@interfaces/stats';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { AMOUNT_TYPES } from '../calculator.constants';
import type { FundLineForm } from '../calculator.types';
import {
  getCalculatorFundDisplayLabel,
  getCalculatorLabel,
  getFundLineAmountError,
  isCalculatorFieldEnabled,
} from '../calculator.utils';

export const CalculatorFundCard = ({
  cardIndex,
  disabled = false,
  foreignExchangeRates,
  fundLines,
  holdings,
  line,
  multipleFundTitle,
  onChange,
  onRemove,
  options,
  resultCurrency,
  showCardChrome = true,
}: {
  cardIndex: number;
  disabled?: boolean;
  foreignExchangeRates: ForeignExchangeRate[];
  fundLines: FundLineForm[];
  holdings: Holding[];
  line: FundLineForm;
  multipleFundTitle?: unknown;
  onChange: (updates: Partial<FundLineForm>) => void;
  onRemove: () => void;
  options: Record<string, unknown>;
  resultCurrency: string;
  showCardChrome?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const selectFundLabel = getCalculatorLabel(
    translateBackendString,
    options,
    'selectFund',
    t('calculator.selectFund'),
  );
  const amountTypeLabel = getCalculatorLabel(
    translateBackendString,
    options,
    'amountType',
    t('calculator.amountType'),
  );

  const usedFundCodes = fundLines
    .map((fundLine, index) =>
      index === cardIndex ? undefined : fundLine.fundCode,
    )
    .filter(Boolean);

  const showAmountField =
    line.amountType === AMOUNT_TYPES.SHARES ||
    line.amountType === AMOUNT_TYPES.DOLLAR_GROSS ||
    line.amountType === AMOUNT_TYPES.DOLLAR_NET;

  const amountError = getFundLineAmountError(
    line,
    holdings,
    resultCurrency,
    foreignExchangeRates,
  );
  const amountErrorText =
    amountError === 'sharesExceed'
      ? t('calculator.errors.sharesExceed')
      : amountError === 'dollarExceed'
        ? t('calculator.errors.dollarExceed')
        : undefined;

  const fundFields = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {isCalculatorFieldEnabled(options.selectFund) && (
        <SelectField
          fullWidth
          label={selectFundLabel}
          value={line.fundCode}
          disabled={disabled || !holdings.length}
          onChange={(event: { target: { value: string } }) =>
            onChange({ fundCode: event.target.value })
          }
          onBlur={() => {}}
          testId={`calculator-fund-select-${cardIndex}`}
        >
          {holdings.map((holding) => {
            const fundId = holding.financialProduct?.id;
            if (!fundId || usedFundCodes.includes(fundId)) return null;
            return (
              <MenuItem key={fundId} value={fundId}>
                {getCalculatorFundDisplayLabel(
                  translateBackendString,
                  holding.financialProduct,
                )}
              </MenuItem>
            );
          })}
        </SelectField>
      )}

      {isCalculatorFieldEnabled(options.amountType) && (
        <SelectField
          fullWidth
          label={amountTypeLabel}
          value={line.amountType}
          disabled={disabled}
          onChange={(event: {
            target: { value: FundLineForm['amountType'] };
          }) =>
            onChange({
              amountType: event.target.value,
              amount: undefined,
            })
          }
          onBlur={() => {}}
          testId={`calculator-amount-type-${cardIndex}`}
        >
          <MenuItem value={AMOUNT_TYPES.ALL_SHARES}>
            {t('calculator.amountTypes.allShares')}
          </MenuItem>
          <MenuItem value={AMOUNT_TYPES.SHARES}>
            {t('calculator.amountTypes.shares')}
          </MenuItem>
          <MenuItem value={AMOUNT_TYPES.DOLLAR_GROSS}>
            {t('calculator.amountTypes.dollarAmount')}
          </MenuItem>
          <MenuItem value={AMOUNT_TYPES.DOLLAR_NET}>
            {t('calculator.amountTypes.dollarNetAmount')}
          </MenuItem>
          <MenuItem value={AMOUNT_TYPES.ANNUAL_FREE_ALLOCATION}>
            {t('calculator.amountTypes.annualFreeAllocation')}
          </MenuItem>
          <MenuItem value={AMOUNT_TYPES.DSC_FREE}>
            {t('calculator.amountTypes.dscFree')}
          </MenuItem>
        </SelectField>
      )}

      {showAmountField && line.amountType === AMOUNT_TYPES.SHARES && (
        <NumberField
          fullWidth
          label={t('calculator.amountLabels.numberOfShares')}
          number={line.amount ?? ''}
          setNumber={(value) => onChange({ amount: value })}
          onBlur={() => {}}
          disabled={disabled}
          unlimitedDecimals
          inputType='decimal'
          error={!!amountErrorText}
          errorText={amountErrorText}
          testId={`calculator-shares-amount-${cardIndex}`}
        />
      )}

      {showAmountField &&
        (line.amountType === AMOUNT_TYPES.DOLLAR_GROSS ||
          line.amountType === AMOUNT_TYPES.DOLLAR_NET) && (
          <AmountField
            fullWidth
            label={t('calculator.amountLabels.amountRequired')}
            amount={line.amount ?? ''}
            setAmount={(value) => onChange({ amount: value })}
            onBlur={() => {}}
            disabled={disabled}
            unlimitedDecimals
            error={!!amountErrorText}
            errorText={amountErrorText}
            testId={`calculator-dollar-amount-${cardIndex}`}
          />
        )}
    </Box>
  );

  if (!showCardChrome) {
    return (
      <Box data-testid={`calculator-fund-card-${cardIndex}`}>{fundFields}</Box>
    );
  }

  const fundTitle = multipleFundTitle
    ? `${translateBackendString(multipleFundTitle)} ${cardIndex + 1}`
    : `${t('calculator.fund')} ${cardIndex + 1}`;

  return (
    <Box
      data-testid={`calculator-fund-card-${cardIndex}`}
      sx={{
        borderRadius: theme.borderRadius.sm,
        p: 2,
        mb: 2,
        bgcolor: theme.color.surfaceContainer,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant='bodySmall' sx={{ fontWeight: 700 }}>
          {fundTitle}
        </Typography>
        {fundLines.length > 1 && (
          <IconButton
            aria-label={t('calculator.removeFund')}
            onClick={onRemove}
            disabled={disabled}
            data-testid={`calculator-remove-fund-${cardIndex}`}
            sx={{ p: 1.25 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} fontSize='inherit' />
          </IconButton>
        )}
      </Box>

      {fundFields}
    </Box>
  );
};
